import { faGoogle, faMeta } from '@fortawesome/free-brands-svg-icons';
import {
  faAnalytics,
  faArrowLeft,
  faArrowRight,
  faArrowRotateLeft,
  faArrowsRotate,
  faArrowsUpDownLeftRight,
  faArrowTrendUp,
  faBan,
  faBoxArchive,
  faBrowser,
  faBullseye,
  faCalendar,
  faCheck,
  faChevronDown,
  faCircleQuestion,
  faCodeMerge,
  faCopy,
  faDownload,
  faEnvelope,
  faExternalLink,
  faEye,
  faFileDownload,
  faFloppyDisk,
  faGear,
  faInboxIn,
  faInfoCircle,
  faInputText,
  faLinkSlash,
  faListCheck,
  faLocation,
  faLockKeyhole,
  faMagnifyingGlass,
  faPause,
  faPencil,
  faPlay,
  faPlus,
  faRectangleAd,
  faSliders,
  faSortAlphaDown,
  faSortAlphaUpAlt,
  faSortNumericDown,
  faSortNumericUpAlt,
  faSparkles,
  faSquareCheck,
  faTrashAlt,
  faTv,
  faUsdCircle,
  faUserGroup,
  faUsers,
  faWavePulse,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { faCircleStop } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import React from 'react';

import textInputIcon from './textInputIcon';

// Create a mapping so we can utilize consistent actions across the app
const actionIconMap = {
  add: faPlus,
  analytics: faAnalytics,
  archive: faBoxArchive,
  arrowDown: faChevronDown,
  back: faArrowLeft,
  cancel: faXmark,
  check: faSquareCheck,
  close: faXmark,
  configure: faSliders,
  create: faPlus,
  customize: faSliders,
  date: faCalendar,
  deepen: faWavePulse,
  delete: faTrashAlt,
  disable: faBan,
  disconnect: faLinkSlash,
  displayAd: faRectangleAd,
  download: faFileDownload,
  drive: faArrowTrendUp,
  duplicate: faCopy,
  edit: faPencil,
  email: faEnvelope,
  export: faDownload,
  google: faGoogle,
  info: faInfoCircle,
  location: faLocation,
  locked: faLockKeyhole,
  lookalike: faUsers,
  magic: faSparkles,
  manage: faListCheck,
  merge: faCodeMerge,
  meta: faMeta,
  monetization: faUsdCircle,
  move: faArrowsUpDownLeftRight,
  next: faArrowRight,
  pause: faPause,
  preview: faExternalLink,
  previous: faArrowLeft,
  publish: faCheck,
  question: faCircleQuestion,
  reach: faBullseye,
  recalculate: faArrowsRotate,
  remove: faXmark,
  rename: faInputText,
  resend: faInboxIn,
  resume: faPlay,
  return: faArrowRotateLeft,
  revoke: faXmark,
  save: faFloppyDisk,
  search: faMagnifyingGlass,
  send: faInboxIn,
  settings: faGear,
  shortLinks: faListCheck,
  stop: faCircleStop,
  template: faBrowser,
  tv: faTv,
  userGroup: faUserGroup,
  view: faEye,
};

const sortIconMap = {
  stringAsc: faSortAlphaDown,
  stringDesc: faSortAlphaUpAlt,
  numberAsc: faSortNumericDown,
  numberDesc: faSortNumericUpAlt,
};

// TODO: Figure out how to install FontAwesomeKitIcons as a package to use custom icons more easily. https://github.com/Feathr/shrike/issues/5047
const customIconMap = {
  rename: textInputIcon,
};

const defaultIcons = { ...actionIconMap, ...sortIconMap };

type TAction = keyof typeof actionIconMap;
type TSort = keyof typeof sortIconMap;
type TCustom = keyof typeof customIconMap;

/*
 * Return the icon component for the given action. This will make a find and replace
 * when we convert to the Icon component much easier and reduce the number if imports
 * in the file the icon is added.
 */
export function getIconForAction(icon: TAction | TSort | TCustom): JSX.Element {
  if (icon in customIconMap) {
    return customIconMap[icon as TCustom]();
  }
  // Use Object.keys to type check icon as TAction | TSort
  if (Object.keys(defaultIcons).includes(icon)) {
    return <FontAwesomeIcon icon={defaultIcons[icon]} />;
  }
  throw new Error(`Icon ${icon} not found`);
}
